// @ts-ignore
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useSession } from '../../../context/SessionContext';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ListAltIcon from '@mui/icons-material/ListAlt'; // Icon for Applications
import AccountCircleIcon from '@mui/icons-material/AccountCircle'; // Icon for Profile
import FolderIcon from '@mui/icons-material/Folder'; // Icon for Files
import LinkIcon from '@mui/icons-material/Link'; // Icon for Linked Applications
import UploadFileIcon from '@mui/icons-material/UploadFile'; // Icon for file upload
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'; // Icon for errors
import styled, { keyframes, css } from 'styled-components'; // Added css import
import { format } from 'date-fns';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle'; // Icon for Thank You page
import LockIcon from '@mui/icons-material/Lock'; // Icon for locked applications
import ApplicationFiles from './ApplicationFiles'; // Import the ApplicationFiles component
import { useUserProfile } from '../../../contexts/UserProfileContext'; // Added for sidebar
import { FaExclamationTriangle } from 'react-icons/fa'; // For modal icon
import { checkAuth, logout } from '../../../utils/authUtils';
import UserProfileSettings from './UserProfileSettings';
import { useSessionTimer } from '../../../contexts/SessionTimerContext';
import ChatWidget from '../../common/ChatWidget';
import { useActivityTracker } from '../../../hooks/useActivityTracker';
import FirstTimeProfileModal from '../../user/FirstTimeProfileModal';
import SocialShareButtons from '../../common/SocialShareButtons';
import PuneMapModal from '../../common/PuneMapModal';

// API URL
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
// Fix API_BASE to properly handle API paths and prevent duplicate /api
const API_BASE = API_URL.endsWith('/api') ? API_URL.slice(0, -4) : API_URL;

// Define the Application interface based on expected data structure
interface Application {
  _id: string; // Assuming MongoDB ObjectId as string
  applicationId: string;
  email: string;
  formData: any; // Or a more specific type if formData structure is consistent
  status: string;
  isComplete?: boolean;
  isSubmitted?: boolean;
  createdAt: string; // Assuming date strings
  updatedAt: string;
  // Add any other fields that are part of the application object fetched from the backend
  history?: Array<{ status: string; changedAt: string; changedBy?: string }>;
  files?: Array<{ fieldname: string; originalname:string; filename: string; path: string }>;
  rejectionReason?: string;
  vehicleDetails?: any;
  dealerInfo?: any;
  customerInfo?: any;
  documents?: any;
  dealershipGroupId?: string | null; // For linked applications
  isPrimaryApplicant?: boolean; // Primary applicant designation
}

// Keyframes for the reveal animation
const revealAnimation = keyframes`
  from {
    clip-path: inset(100% 0 0 0);
  }
  to {
    clip-path: inset(0 0 0 0);
  }
`;

// Styled component for the animated image
const AnimatedProfileImage = styled.img`
  animation: ${revealAnimation} 2s cubic-bezier(0.25, 0.1, 0.25, 1) forwards;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
`;

// Styled Components
const Container = styled.div`
  display: flex;
  min-height: 100vh;
  min-height: -webkit-fill-available;
  background: linear-gradient(135deg, #f0f4ff 0%, #e6f0ff 100%);
  font-family: 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  overflow-x: hidden;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Sidebar = styled.div`
  width: 280px;
  background: linear-gradient(180deg, #2c3e50 0%, #1a2533 100%);
  color: white;
  padding: 0 0 1.5rem;
  box-shadow: 4px 0 10px rgba(0, 0, 0, 0.1);
  border-radius: 0 20px 20px 0;
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle at 30% 20%, rgba(76, 110, 245, 0.1) 0%, transparent 50%);
    pointer-events: none;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    border-radius: 0 0 20px 20px;
    padding: 1rem 0 0;
  }
`;

const Logo = styled(motion.div)` /* Added motion */
  padding: 0 0.8rem;
  margin-bottom: 0;
  text-align: center;
  
  img {
    max-width: 266px;
    max-height: 103px;
    height: auto;
    object-fit: contain;
  }
`;

const NavLink = styled.div<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  padding: 0.9rem 1.5rem; /* Increased padding for better touch targets */
  margin: 0.3rem 0;
  color: ${props => props.isActive ? 'white' : '#cbd5e0'}; /* Adjusted inactive color */
  background-color: ${props => props.isActive ? 'rgba(76, 110, 245, 0.2)' : 'transparent'}; /* Slightly stronger active bg */
  border-left: 4px solid ${props => props.isActive ? '#4c6ef5' : 'transparent'};
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  font-size: 0.95rem;
  font-weight: 500;

  svg {
    margin-right: 1rem; /* Increased icon spacing */
    width: 22px;
    height: 22px;
    stroke-width: 2;
    color: ${props => props.isActive ? 'white' : '#a0aec0'}; /* Adjusted icon color */
  }

  &:hover {
    color: white;
    background-color: rgba(76, 110, 245, 0.1);
  }
`;

const SidebarTitle = styled.h2`
  font-size: 0.9rem; /* Subtler title */
  color: #a0aec0; /* Lighter color */
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 1rem;
  font-weight: 600;
  padding: 0 1.5rem; /* Align with NavLink */
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
  padding: 1rem 1.5rem; /* Adjusted padding */
  border-radius: 8px;
  min-height: 70px; // Ensure space for image and text
`;

// New Styled Components for Profile Image and Details in Sidebar
const ProfileImageContainer = styled(motion.div)`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  margin-right: 15px;
  overflow: hidden;
  position: relative;
  border: 2px solid #4A5568;
  background-color: #334155; // Fallback background
  flex-shrink: 0; // Prevent shrinking
`;

const ProfileImage = styled(motion.img)`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
`;

const ScannerEffect = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 5px;
  background: rgba(76, 110, 245, 0.7);
  box-shadow: 0 0 10px rgba(76, 110, 245, 0.7);
  border-radius: 2px;
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;
`;

const UserName = styled.div`
  font-size: 0.95rem;
  font-weight: 600;
  color: #e2e8f0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UserEmailStyled = styled.div`
  font-size: 0.75rem;
  color: #a0aec0;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SessionTimerBadge = styled.div<{ $isWarning: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: ${props => props.$isWarning 
    ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' 
    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  color: white;
  padding: 8px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px ${props => props.$isWarning 
    ? 'rgba(245, 158, 11, 0.3)' 
    : 'rgba(102, 126, 234, 0.3)'};
  margin: 1rem 1.5rem;
  width: calc(100% - 3rem);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px ${props => props.$isWarning 
      ? 'rgba(245, 158, 11, 0.4)' 
      : 'rgba(102, 126, 234, 0.4)'};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const LogoutButton = styled(motion.button)`
  background-color: transparent;
  color: #f56565; /* Lighter red for dark bg */
  border: 2px solid #f56565;
  border-radius: 8px;
  padding: 0.7rem 1rem; /* Adjusted padding */
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center; /* Center content */
  gap: 8px;
  width: calc(100% - 3rem); /* Adjust width considering sidebar padding */
  margin: 0.5rem 1.5rem 0 1.5rem; /* Reduced top margin since timer is above */

  svg {
    width: 18px;
    height: 18px;
    stroke-width: 2;
  }

  &:hover {
    background-color: #f56565;
    color: white;
    border-color: #f56565;
    transform: translateY(-2px);
    box-shadow: 0 4px 10px rgba(245, 101, 101, 0.2);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 5px rgba(245, 101, 101, 0.15);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(245, 101, 101, 0.3);
  }
`;

const Content = styled.div`
  flex: 1;
  padding: 2.5rem;
  overflow-y: auto;
  position: relative;
  -webkit-overflow-scrolling: touch;
  
  &::after {
    content: '';
    position: fixed;
    bottom: 0;
    right: 0;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle at bottom right, rgba(76, 110, 245, 0.05) 0%, transparent 70%);
    pointer-events: none;
    z-index: -1;
  }
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2.5rem;
  padding-bottom: 1.2rem;
  border-bottom: 1px solid rgba(226, 232, 240, 0.8);
  position: relative;
  flex-wrap: wrap;
  gap: 1rem;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    width: 100px;
    height: 3px;
    background: linear-gradient(135deg, #4c6ef5 0%, #3b5bdb 100%);
    border-radius: 3px;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }
`;

const Title = styled(motion.h1)`
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
  background: linear-gradient(135deg, #4c6ef5 0%, #3b5bdb 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-fill-color: transparent;
  letter-spacing: -0.5px;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.25rem;
    text-align: center;
  }
`;

const CreateButton = styled(motion.button)`
  background-color: #4361ee;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 48px;
  -webkit-tap-highlight-color: transparent;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    background-color: #3a56d4;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(67, 97, 238, 0.2);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 6px rgba(67, 97, 238, 0.15);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(67, 97, 238, 0.3);
  }
  
  @media (max-width: 480px) {
    width: 100%;
    padding: 0.875rem 1rem;
  }
`;

/* ── Land Confirmation Modal ── */
const LandModalOverlay = styled.div`
  position: fixed; inset: 0; z-index: 10000;
  background: rgba(0,0,0,0.55); backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
  animation: lmFadeIn 0.25s ease;
  @keyframes lmFadeIn { from { opacity: 0; } to { opacity: 1; } }
`;
const landModalPop = keyframes`
  0% { transform: scale(0.85) translateY(20px); opacity: 0; }
  60% { transform: scale(1.02) translateY(-2px); opacity: 1; }
  100% { transform: scale(1) translateY(0); opacity: 1; }
`;
const LandModalBox = styled.div`
  background: #fff; border-radius: 20px; padding: 32px 28px 28px;
  max-width: 440px; width: 92%; box-shadow: 0 20px 60px rgba(0,0,0,0.25);
  animation: ${landModalPop} 0.35s ease; text-align: center; position: relative;
`;
const LandModalIcon = styled.div`
  width: 64px; height: 64px; border-radius: 18px; margin: 0 auto 16px;
  display: flex; align-items: center; justify-content: center; font-size: 2rem;
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  box-shadow: 0 6px 20px rgba(59,130,246,0.2);
`;
const LandModalTitle = styled.h3`
  margin: 0 0 8px; font-size: 1.15rem; font-weight: 800; color: #1e293b;
`;
const LandModalDesc = styled.p`
  margin: 0 0 20px; font-size: 0.88rem; color: #64748b; line-height: 1.6;
`;
const landPulse = keyframes`
  0%, 100% { box-shadow: 0 4px 14px rgba(34,197,94,0.25); }
  50% { box-shadow: 0 4px 24px rgba(34,197,94,0.5); }
`;
const LandModalYesBtn = styled.button`
  width: 100%; padding: 13px 20px; border: none; border-radius: 12px; cursor: pointer;
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%); color: #fff;
  font-size: 0.95rem; font-weight: 700; margin-bottom: 10px;
  transition: all 0.3s; animation: ${landPulse} 2s ease infinite;
  &:hover { transform: translateY(-1px); filter: brightness(1.05); }
`;
const LandModalNoBtn = styled.button`
  width: 100%; padding: 12px 20px; border: 1.5px solid #e2e8f0; border-radius: 12px;
  cursor: pointer; background: #fff; color: #64748b; font-size: 0.88rem; font-weight: 600;
  transition: all 0.3s; margin-bottom: 8px;
  &:hover { background: #fef2f2; border-color: #fca5a5; color: #dc2626; }
`;
const LandModalDismiss = styled.button`
  background: none; border: none; color: #94a3b8; font-size: 0.78rem; cursor: pointer;
  padding: 6px 12px; transition: color 0.2s;
  &:hover { color: #475569; }
`;
const LandNoResultBox = styled.div`
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  border: 1.5px solid #fca5a5; border-radius: 14px; padding: 20px; margin-top: 6px;
  text-align: center;
`;
/* ── end Land Modal ── */

const Card = styled(motion.div)`
  background-color: white;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(76, 110, 245, 0.1);
  padding: 2rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    padding: 1.25rem;
    border-radius: 12px;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
    margin-bottom: 1rem;
  }
`;

const ApplicationsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 1.5rem;
  
  th, td {
    padding: 1rem 1.2rem;
    text-align: left;
    border-bottom: 1px solid #e2e8f0;
  }
  
  th {
    font-size: 0.75rem;
    font-weight: 600;
    color: #4a5568;
    text-transform: uppercase;
    background: linear-gradient(135deg, #f8faff 0%, #f0f4ff 100%);
    letter-spacing: 0.05em;
  }
  
  tbody tr:hover {
    background-color: #f8fafc;
  }
  
  @media (max-width: 768px) {
    display: block;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    
    th, td {
      padding: 0.75rem 0.5rem;
      font-size: 0.8rem;
      white-space: nowrap;
    }
  }
  
  @media (max-width: 480px) {
    th, td {
      padding: 0.5rem 0.4rem;
      font-size: 0.75rem;
    }
  }
`;

const StatusBadge = styled.span<{ status: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.35rem 0.8rem;
  border-radius: 9999px;
  font-size: 0.8rem;
  font-weight: 500;
  color: white;
  text-transform: capitalize;
  white-space: nowrap;

  ${({ status }) => {
    switch (status.toLowerCase()) {
      case 'draft':
        return css`background-color: #6b7280;`; // Gray
      case 'submitted':
        return css`background-color: #2563eb;`; // Blue
      case 'under review':
        return css`background-color: #9333ea;`; // Purple
      case 'approved':
        return css`background-color: #16a34a;`; // Green
      case 'payment_complete':
        return css`
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
          font-weight: 600;
        `; // Vibrant green gradient with shadow
      case 'rejected':
        return css`background-color: #dc2626;`; // Red
      case 'queued for deletion':
        return css`background-color: #f59e0b;`; // Amber
      default:
        return css`background-color: #6b7280;`; // Gray for unknown
    }
  }}
`;

const StyledActionButton = styled(motion.button)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  border: none;
  background-color: rgba(0, 0, 0, 0.05);
  color: #4b5563;
  cursor: pointer;
  transition: all 0.2s ease;
  transition: background-color 0.2s ease, color 0.2s ease;
  margin-right: 8px; /* Add some spacing between buttons */
  font-size: 0.875rem;
  font-weight: 500;

  &:last-child {
    margin-right: 0;
  }

  svg {
    width: 18px;
    height: 18px;
    fill: currentColor;
  }

  &:hover:not(:disabled) {
    background-color: rgba(76, 110, 245, 0.1); /* Light primary blue background */
    color: #3b5bdb; /* Darker primary blue text */
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    color: #a0aec0; /* Muted text color for disabled state */
    background-color: transparent;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
`;

const LoadingSpinner = styled.div`
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top: 3px solid #4a6cf7;
  width: 30px;
  height: 30px;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center; // Keep for text content within child block elements
  padding: 40px 20px;
  color: #718096;
  
  svg {
    width: 60px;
    height: 60px;
    color: #cbd5e0;
    margin-bottom: 20px;
  }
  
  h3 {
    font-size: 18px;
    margin-bottom: 10px;
    color: #4a5568;
  }
  
  p {
    font-size: 14px;
    margin-bottom: 20px;
  }
`;

const UserDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { timeLeft, isWarning, formatTime, setShowModal } = useSessionTimer();
  // const [userEmail, setUserEmail] = useState<string | null>(null); // Replaced by context
  const {
    profileImageUrl,
    animatingImageUrl,
    fullName,
    email: userEmailFromContext,
    phoneNumber: userPhoneFromContext,
    getFormattedName,
    completeImageAnimation,
    isLoadingProfile: isUserProfileLoading,
    isAdmin: isAdminProfile, // Flag from context to detect admin
  } = useUserProfile();
  const [applications, setApplications] = useState<Application[]>([]);
  const [selectedApplication, setSelectedApplication] = useState<Application | null>(null);
  const [linkedCounts, setLinkedCounts] = useState<{ [key: string]: number }>({});
  const [linkedApplicationsDetails, setLinkedApplicationsDetails] = useState<any[]>([]);
  const [isLoadingLinked, setIsLoadingLinked] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<'applications' | 'files' | 'profile' | 'linked'>('applications');
  const [showLinkedApplicationsNav, setShowLinkedApplicationsNav] = useState<boolean>(true);
  const [selectedApplicationId, setSelectedApplicationId] = useState<string | null>(null);
  const [lockedApplications, setLockedApplications] = useState<Set<string>>(new Set());
  const [appPage, setAppPage] = useState(1);
  const [appPageSize, setAppPageSize] = useState<number | 'all'>(5);
  const [showPuneMapModal, setShowPuneMapModal] = useState(false);

  // Land ownership confirmation modal state
  const [showLandConfirmModal, setShowLandConfirmModal] = useState(false);
  const [landConfirmResult, setLandConfirmResult] = useState<'pending' | 'no'>('pending');

  // Use shared session context for session expiry handling
  const { triggerSessionExpired } = useSession();

  // Track user activity for admin visibility
  const { updateActivity } = useActivityTracker({ 
    page: 'Dashboard', 
    activity: activeView === 'applications' ? 'Viewing Applications' : 
              activeView === 'files' ? 'Viewing Files' : 
              activeView === 'profile' ? 'Viewing Profile' : 
              activeView === 'linked' ? 'Viewing Linked Apps' : 'Browsing'
  });

  // Update activity when view changes
  useEffect(() => {
    const activityMap: Record<string, string> = {
      'applications': 'Viewing Applications',
      'files': 'Viewing Files',
      'profile': 'Viewing Profile',
      'linked': 'Viewing Linked Apps'
    };
    updateActivity(activityMap[activeView] || 'Browsing');
  }, [activeView, updateActivity]);

  // Detect if admin is accessing user dashboard and redirect to admin dashboard
  useEffect(() => {
    if (!isUserProfileLoading && isAdminProfile) {
      console.log('[UserDashboard] Admin profile detected, redirecting to admin dashboard');
      navigate('/admin/dashboard', { replace: true });
    }
  }, [isAdminProfile, isUserProfileLoading, navigate]);

  // Clear editingUser from sessionStorage when user returns to dashboard
  // This ensures the "Who is filling the form today?" modal appears every time user resumes a draft application
  useEffect(() => {
    sessionStorage.removeItem('editingUser');
    console.log('[UserDashboard] Cleared editingUser from sessionStorage');
  }, []);

  // Check authentication on component mount
  useEffect(() => {
    const verifyAuth = async () => {
      try {
        // First check if we have user profile data in session storage
        const profileData = sessionStorage.getItem('userProfileData');
        
        if (profileData) {
          console.log('[UserDashboard] Using profile data from session storage for authentication');
          // We have profile data, so we're authenticated
          fetchApplications();
          return;
        }
        
        console.log('[UserDashboard] No session data found, checking auth via API');
        // Fallback to API auth check
        const { authenticated } = await checkAuth();
        
        if (!authenticated) {
          console.log('[UserDashboard] Auth check failed, redirecting to login');
          // Redirect to email verification if not authenticated
          navigate('/');
          return;
        }
        
        // Fetch user applications
        fetchApplications();
      } catch (error) {
        console.error('[UserDashboard] Auth check error:', error);
        
        // Even if API check fails, check if we have session data as a last resort
        const profileData = sessionStorage.getItem('userProfileData');
        if (profileData) {
          console.log('[UserDashboard] API auth failed but using session data as fallback');
          fetchApplications();
          return;
        }
        
        navigate('/');
      }
    };
    
    verifyAuth();
  }, [navigate]);

  // Load Linked Applications navigation visibility from backend settings
  useEffect(() => {
    const fetchLinkedNavSetting = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/public/settings`);
        if (!response.ok) {
          console.error('Failed to fetch settings for Linked Applications navigation:', response.status, response.statusText);
          return;
        }
        const data = await response.json();
        const settings = data.settings || data;
        const value = settings && typeof settings.showLinkedApplicationsNav === 'boolean'
          ? settings.showLinkedApplicationsNav
          : true;
        setShowLinkedApplicationsNav(value);
      } catch (error) {
        console.error('Error fetching Linked Applications navigation setting:', error);
      }
    };

    fetchLinkedNavSetting();
  }, []);

  // Navigate to application form like view mode, but with auto-download flag
  const handleDownloadApplication = async (application: Application) => {
    if (!application) return;
    
    try {
      // Verify application still exists before navigating
      const response = await axios.get(`${API_BASE}/api/applications/${application.applicationId}`, { withCredentials: true });
      
      if (!response.data || !response.data.application) {
        setApplications(prev => prev.filter(app => app.applicationId !== application.applicationId));
        alert('This application no longer exists.');
        return;
      }
      
      // Store the application ID in session storage (same as view mode)
      sessionStorage.setItem('currentApplicationId', application.applicationId);
      
      // Clear admin mode to ensure user doesn't see admin back button
      sessionStorage.removeItem('adminEditMode');
      
      // Set view mode in session storage (same as view mode)
      sessionStorage.setItem('viewMode', 'true');
      
      // Set auto-download flag in session storage
      sessionStorage.setItem('autoDownloadPdf', 'true');
      
      // Navigate directly to Page 1 (same route as eye icon) to ensure data loads properly
      navigate('/form/page1');
    } catch (err: any) {
      if (err.response?.status === 404) {
        setApplications(prev => prev.filter(app => app.applicationId !== application.applicationId));
        alert('This application no longer exists or has been deleted.');
      } else {
        console.error('Error accessing application for download:', err);
        alert('Error accessing application. Please try again.');
      }
    }
  };

  // Fetch user applications
  // Fetch linked application counts
  const fetchLinkedCounts = async (appsWithGroups: Application[]) => {
    const counts: { [key: string]: number } = {};
    
    for (const app of appsWithGroups) {
      try {
        const response = await axios.get(
          `${API_BASE}/api/applications/${app.applicationId}/linked`,
          { withCredentials: true }
        );
        
        if (response.data.success) {
          counts[app.applicationId] = response.data.count || 0;
        }
      } catch (error) {
        console.error(`Error fetching linked count for ${app.applicationId}:`, error);
      }
    }
    
    setLinkedCounts(counts);
  };
  
  // Fetch all linked applications with details
  const fetchLinkedApplicationsDetails = async () => {
    setIsLoadingLinked(true);
    setError(null);
    
    try {
      // First, fetch user's applications to ensure we have latest data
      axios.defaults.withCredentials = true;
      const appsResponse = await axios.get(`${API_BASE}/api/applications`);
      
      if (!appsResponse.data.success || !appsResponse.data.applications) {
        setLinkedApplicationsDetails([]);
        setIsLoadingLinked(false);
        return;
      }
      
      const userApps = appsResponse.data.applications;
      const appsWithGroups = userApps.filter((app: Application) => app.dealershipGroupId);
      
      if (appsWithGroups.length === 0) {
        setLinkedApplicationsDetails([]);
        setIsLoadingLinked(false);
        return;
      }
      
      const allLinkedDetails: any[] = [];
      
      for (const app of appsWithGroups) {
        // Add user's own application first with masked phone
        const ownPhone = app.formData?.personalMobile || '';
        const maskedOwnPhone = ownPhone.length >= 4 ? `****${ownPhone.slice(-4)}` : ownPhone;
        const applicantName = app.formData?.nameInBlocks || '';
        
        allLinkedDetails.push({
          applicationId: app.applicationId,
          isPrimaryApplicant: app.isPrimaryApplicant,
          status: app.status,
          createdAt: app.createdAt,
          fullName: applicantName || 'Name not provided',
          phoneNumber: maskedOwnPhone || 'Phone not provided',
          groupId: app.dealershipGroupId,
          isOwnApp: true
        });
        
        try {
          const response = await axios.get(
            `${API_BASE}/api/applications/${app.applicationId}/linked`,
            { withCredentials: true }
          );
          
          if (response.data.success && response.data.applications) {
            // Add linked applications with group info
            response.data.applications.forEach((linkedApp: any) => {
              allLinkedDetails.push({
                ...linkedApp,
                groupId: app.dealershipGroupId,
                relatedToMyApp: app.applicationId,
                isOwnApp: false
              });
            });
          }
        } catch (error) {
          console.error(`Error fetching linked details for ${app.applicationId}:`, error);
        }
      }
      
      // Remove duplicates based on applicationId (keep first occurrence)
      const uniqueLinked = allLinkedDetails.filter((app, index, self) =>
        index === self.findIndex((t) => t.applicationId === app.applicationId)
      );
      
      // Sort: Primary first, then by creation date
      uniqueLinked.sort((a, b) => {
        if (a.isPrimaryApplicant && !b.isPrimaryApplicant) return -1;
        if (!a.isPrimaryApplicant && b.isPrimaryApplicant) return 1;
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      });
      
      setLinkedApplicationsDetails(uniqueLinked);
    } catch (error) {
      console.error('Error fetching linked applications details:', error);
      setError('Failed to load linked applications');
    } finally {
      setIsLoadingLinked(false);
    }
  };
  
  // Fetch lock status for all applications
  const fetchLockStatuses = async (apps: Application[]) => {
    try {
      const lockedSet = new Set<string>();
      for (const app of apps) {
        const response = await axios.get(`${API_BASE}/api/settings/application-lock-status/${app.applicationId}`);
        if (response.data.success && response.data.isLocked) {
          lockedSet.add(app.applicationId);
        }
      }
      setLockedApplications(lockedSet);
    } catch (error) {
      console.error('Error fetching lock statuses:', error);
    }
  };

  const fetchApplications = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Configure axios to send credentials (cookies)
      axios.defaults.withCredentials = true;
      
      // Fetch applications from backend API
      const response = await axios.get(`${API_BASE}/api/applications`);
      
      if (response.data.success) {
        // Add console log to debug application status values
        console.log('Application statuses:', response.data.applications.map(app => app.status));
        setApplications(response.data.applications);
        
        // Set currentApplicationId for live session streaming (use most recent app)
        if (response.data.applications.length > 0 && !sessionStorage.getItem('currentApplicationId')) {
          const mostRecent = response.data.applications[0];
          if (mostRecent.applicationId) {
            sessionStorage.setItem('currentApplicationId', mostRecent.applicationId);
          }
        }

        // Fetch lock status for all applications
        fetchLockStatuses(response.data.applications);
        
        // Fetch linked counts for applications that are in a group
        const appsWithGroups = response.data.applications.filter((app: Application) => app.dealershipGroupId);
        if (appsWithGroups.length > 0) {
          fetchLinkedCounts(appsWithGroups);
        }
      } else {
        setError('Failed to fetch applications');
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
      setError('Failed to fetch applications. Please try again.');
      if (axios.isAxiosError(error) && error.response && (error.response.status === 401 || error.response.status === 403)) {
        triggerSessionExpired();
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Function to generate the new application ID format
  const generateNewApplicationId = (): string => {
    const now = new Date();
    const dd = String(now.getDate()).padStart(2, '0');
    const mm = String(now.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
    const yy = String(now.getFullYear()).slice(-2); // Last two digits of year
    const hr = String(now.getHours()).padStart(2, '0');
    const min = String(now.getMinutes()).padStart(2, '0');
    const sec = String(now.getSeconds()).padStart(2, '0');
    
    // Always use PN as the location code as per requirements
    const locationCode = "PN";
    // Start with 01 as the serial number
    const serialNumberString = "01";
    
    // Format: CNG-DDMMYY-HHMMSS-PN/XX
    return `CNG-${dd}${mm}${yy}-${hr}${min}${sec}-${locationCode}/${serialNumberString}`;
  };

  // Handle create new application — shows land confirmation modal first
  const handleCreateApplication = () => {
    setLandConfirmResult('pending');
    setShowLandConfirmModal(true);
  };

  // Proceed after user confirms land ownership in Pune
  const handleLandConfirmed = () => {
    setShowLandConfirmModal(false);
    setLandConfirmResult('pending');
    const newAppId = generateNewApplicationId();
    sessionStorage.removeItem('currentApplicationId');
    navigate('/application-form', { state: { newApplicationId: newAppId } });
  };

  // Handle edit application
  const handleEditApplication = async (applicationId: string) => {
    try {
      // Verify application still exists before navigating
      const response = await axios.get(`${API_BASE}/api/applications/${applicationId}`, { withCredentials: true });
      
      if (!response.data.success || !response.data.application) {
        // Application no longer exists - remove from local state and show error
        setApplications(prev => prev.filter(app => app.applicationId !== applicationId));
        setError('This application no longer exists. It may have been deleted by an administrator.');
        return;
      }
      
      // Store application ID in session storage
      sessionStorage.setItem('currentApplicationId', applicationId);
      
      // Clear admin mode to ensure user doesn't see admin back button
      sessionStorage.removeItem('adminEditMode');
      
      // Clear view mode to ensure form is editable
      sessionStorage.removeItem('viewMode');
      
      // Navigate directly to Page 1 to ensure loading modal appears consistently
      navigate('/form/page1');
    } catch (err: any) {
      if (err.response?.status === 404) {
        // Application not found - remove from local state
        setApplications(prev => prev.filter(app => app.applicationId !== applicationId));
        setError('This application no longer exists. It may have been deleted by an administrator.');
      } else {
        setError('Failed to access application. Please try again.');
      }
    }
  };

  // Handle view application
  const handleViewApplication = async (applicationId: string) => {
    try {
      // Verify application still exists before navigating
      const response = await axios.get(`${API_BASE}/api/applications/${applicationId}`, { withCredentials: true });
      
      if (!response.data.success || !response.data.application) {
        // Application no longer exists - remove from local state and show error
        setApplications(prev => prev.filter(app => app.applicationId !== applicationId));
        setError('This application no longer exists. It may have been deleted by an administrator.');
        return;
      }
      
      // Store application ID in session storage, same as edit function
      sessionStorage.setItem('currentApplicationId', applicationId);
      
      // Clear admin mode to ensure user doesn't see admin back button
      sessionStorage.removeItem('adminEditMode');
      
      // Set view mode in session storage
      sessionStorage.setItem('viewMode', 'true');
      
      // Navigate directly to Page 1 to ensure prefilled data loads immediately
      navigate('/form/page1');
    } catch (err: any) {
      if (err.response?.status === 404) {
        setApplications(prev => prev.filter(app => app.applicationId !== applicationId));
        setError('This application no longer exists. It may have been deleted by an administrator.');
      } else {
        setError('Failed to access application. Please try again.');
      }
    }
  };
  
  // Handle view thank you page
  const handleViewThankYou = (applicationId: string) => {
    navigate(`/thank-you/${applicationId}`);
  };

  // Handle initiating the deletion modal for a draft
  const handleDeleteDraft = (appId: string) => {
    setApplicationToDeleteId(appId);
    setShowDeleteModal(true);
    setDeleteConfirmText(''); // Clear previous input
  };

  const handleConfirmDeleteDraft = async () => {
    if (!applicationToDeleteId || deleteConfirmText !== 'DELETE') {
      setError('Deletion confirmation failed. Please type DELETE correctly.');
      return;
    }
    setIsLoading(true); // Consider a specific loading state for this action
    try {
      axios.defaults.withCredentials = true;
      // IMPORTANT: This assumes a new backend endpoint for hard deleting drafts
      const response = await axios.delete(`${API_BASE}/api/applications/draft/${applicationToDeleteId}`);
      if (response.data.success) {
        setApplications(prevApplications => prevApplications.filter(app => app.applicationId !== applicationToDeleteId));
        setShowDeleteModal(false);
        setApplicationToDeleteId(null);
        // Optionally, show a success message
      } else {
        setError(response.data.message || 'Failed to delete draft application.');
      }
    } catch (err: any) {
      console.error('Error deleting draft application:', err);
      if (err.response?.status === 404) {
        // Application already deleted - remove from local state
        setApplications(prevApplications => prevApplications.filter(app => app.applicationId !== applicationToDeleteId));
        setShowDeleteModal(false);
        setApplicationToDeleteId(null);
        setError('This application no longer exists. It may have been deleted by an administrator.');
      } else {
        setError(err.response?.data?.message || 'An error occurred while deleting the draft.');
      }
    } finally {
      setIsLoading(false);
      setShowDeleteModal(false); // Ensure modal closes even on error, or handle differently
    }
  };

  const handleRequestDeletion = async (applicationId: string) => {
    try {
      // Configure axios to send credentials (cookies)
      axios.defaults.withCredentials = true;
      
      // Request application deletion
      const response = await axios.put(`${API_BASE}/api/applications/${applicationId}/status`, {
        status: 'Queued for deletion'
      });
      
      if (response.data.success) {
        // Update application status in state
        setApplications(applications.map(app => 
          app.applicationId === applicationId 
            ? { ...app, status: 'Queued for deletion' } 
            : app
        ));
      } else {
        setError('Failed to request deletion');
      }
    } catch (error) {
      console.error('Error requesting deletion:', error);
      setError('Failed to request deletion. Please try again.');
    }
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      const { success } = await logout();
      
      if (success) {
        // Redirect to home page
        navigate('/');
      }
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // State for delete confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [applicationToDeleteId, setApplicationToDeleteId] = useState<string | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState<string>('');

  // First-time profile setup state — check only once on initial load
  const [profileSetupDone, setProfileSetupDone] = useState(false);
  const [profileChecked, setProfileChecked] = useState(false);

  useEffect(() => {
    if (!isUserProfileLoading && !profileChecked) {
      setProfileChecked(true);
      const trimmedName = (fullName || '').trim();
      const isRealName = trimmedName && trimmedName.toLowerCase() !== 'new user' && trimmedName.split(/\s+/).length >= 2;
      if (isRealName && profileImageUrl) {
        setProfileSetupDone(true);
      }
    }
  }, [isUserProfileLoading, fullName, profileImageUrl, profileChecked]);

  const needsProfileSetup = profileChecked && !profileSetupDone;

  // Land eligibility guidance state (shown after profile setup, before first application)
  const [eligibilityAcknowledged, setEligibilityAcknowledged] = useState(false);
  const showEligibilityGuide = !isLoading && !needsProfileSetup && applications.length === 0 && !eligibilityAcknowledged;

  // getAvatarLetter function removed, using context data for avatar/initials

  return (
    <>
    {/* First-time profile setup modal */}
    {needsProfileSetup && (
      <FirstTimeProfileModal onComplete={() => setProfileSetupDone(true)} />
    )}

    {/* ── Land Ownership Confirmation Modal ── */}
    {showLandConfirmModal && (
      <LandModalOverlay onClick={(e) => { if (e.target === e.currentTarget) { setShowLandConfirmModal(false); setLandConfirmResult('pending'); } }}>
        <LandModalBox>
          {landConfirmResult === 'pending' ? (
            <>
              <LandModalIcon>🏗️</LandModalIcon>
              <LandModalTitle>Do you own or have access to land in Pune?</LandModalTitle>
              <LandModalDesc>
                To apply for an MNGL CNG Dealership, you must have land available in <strong>Pune</strong> that
                falls under MNGL's <strong>advertised locations</strong>.<br /><br />
                <span style={{ fontSize: '0.82rem', color: '#94a3b8', display: 'block', marginBottom: '6px' }}>
                  <strong style={{ color: '#64748b' }}>हिंदी:</strong> क्या आपके पास पुणे में ज़मीन है? MNGL CNG डीलरशिप के लिए आवेदन करने हेतु आपके पास पुणे में विज्ञापित स्थान पर ज़मीन होना आवश्यक है।
                </span>
                <span style={{ fontSize: '0.82rem', color: '#94a3b8', display: 'block' }}>
                  <strong style={{ color: '#64748b' }}>मराठी:</strong> तुमच्याकडे पुण्यात जमीन आहे का? MNGL CNG डीलरशिपसाठी अर्ज करण्यासाठी तुमच्याकडे पुण्यातील जाहिरात केलेल्या ठिकाणी जमीन असणे आवश्यक आहे.
                </span>
              </LandModalDesc>
              <LandModalYesBtn onClick={handleLandConfirmed}>
                ✅ Yes, I have land in Pune — Proceed
              </LandModalYesBtn>
              <LandModalNoBtn onClick={() => setLandConfirmResult('no')}>
                ❌ No, I don't have land in Pune
              </LandModalNoBtn>
              <LandModalDismiss onClick={() => { setShowLandConfirmModal(false); setLandConfirmResult('pending'); }}>
                Cancel
              </LandModalDismiss>
            </>
          ) : (
            <>
              <LandModalIcon style={{ background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)' }}>🚫</LandModalIcon>
              <LandModalTitle style={{ color: '#dc2626' }}>Application Not Possible</LandModalTitle>
              <LandNoResultBox>
                <div style={{ fontSize: '0.88rem', color: '#991b1b', fontWeight: 600, marginBottom: '10px' }}>
                  Unfortunately, you need to have land in Pune to apply for an MNGL CNG Dealership.
                </div>
                <div style={{ fontSize: '0.82rem', color: '#7f1d1d', lineHeight: 1.7 }}>
                  <strong>हिंदी:</strong> दुर्भाग्यवश, MNGL CNG डीलरशिप के लिए आवेदन करने हेतु आपके पास पुणे में ज़मीन होना अनिवार्य है।
                </div>
                <div style={{ fontSize: '0.82rem', color: '#7f1d1d', lineHeight: 1.7, marginTop: '6px' }}>
                  <strong>मराठी:</strong> दुर्दैवाने, MNGL CNG डीलरशिपसाठी अर्ज करण्यासाठी तुमच्याकडे पुण्यात जमीन असणे अनिवार्य आहे.
                </div>
              </LandNoResultBox>
              <div style={{ marginTop: '16px', padding: '14px', background: '#f0f4ff', borderRadius: '12px', border: '1px solid #dbeafe' }}>
                <div style={{ fontSize: '0.82rem', color: '#1e40af', fontWeight: 600, marginBottom: '8px' }}>
                  💡 Have questions? We can help!
                </div>
                <div style={{ fontSize: '0.78rem', color: '#475569', lineHeight: 1.7 }}>
                  📞 Call: <strong>095525 71239</strong> / <strong>094041 52755</strong><br/>
                  ✉️ Email: <a href="mailto:cngdealership2026@mngl.in" style={{ color: '#2563eb', fontWeight: 600 }}>cngdealership2026@mngl.in</a>
                </div>
              </div>
              <LandModalDismiss onClick={() => { setShowLandConfirmModal(false); setLandConfirmResult('pending'); }} style={{ marginTop: '14px' }}>
                Close
              </LandModalDismiss>
            </>
          )}
        </LandModalBox>
      </LandModalOverlay>
    )}

    <Container>
      <Sidebar>
        <div style={{ background: 'rgba(255,255,255,0.92)', padding: '1rem 0 0', marginBottom: '0', borderRadius: '0 20px 0 0' }}>
          <Logo>
            <img src="/assets/logo.png" alt="CNG Dealership Logo" />
          </Logo>
          <div style={{ height: '1.5px', background: 'linear-gradient(90deg, transparent 5%, rgba(100,116,139,0.35) 50%, transparent 95%)', margin: '0.8rem 0 0' }} />
        </div>
        
        <SidebarTitle style={{ marginTop: '1rem' }}>Navigation</SidebarTitle>
        <NavLink isActive={activeView === 'applications'} onClick={() => setActiveView('applications')}>
          <ListAltIcon />
          My Applications
        </NavLink>
        {showLinkedApplicationsNav && (
          <NavLink isActive={activeView === 'linked'} onClick={() => {
            setActiveView('linked');
            fetchLinkedApplicationsDetails();
          }}>
            <LinkIcon />
            Linked Applications
          </NavLink>
        )}
        <NavLink isActive={activeView === 'files'} onClick={() => {
          setActiveView('files');
          // Clear selected application when switching to file management view
          setSelectedApplicationId(null);
        }}>
          <FolderIcon />
          File Management
        </NavLink>
        <NavLink isActive={activeView === 'profile'} onClick={() => setActiveView('profile')}>
          <AccountCircleIcon />
          Profile Settings
        </NavLink>

        <SidebarTitle style={{ marginTop: '2rem' }}>Account</SidebarTitle>
        {isUserProfileLoading ? (
          <UserInfo>
             <ProfileImageContainer>
                {/* Skeleton loader for image */}
                <div style={{width: '100%', height: '100%', backgroundColor: '#4A5568', borderRadius: '50%'}} />
             </ProfileImageContainer>
             <UserDetails>
                <UserName style={{width: '100px', height: '1em', backgroundColor: '#4A5568', borderRadius: '4px', marginBottom: '0.3em'}} />
                <UserEmailStyled style={{width: '150px', height: '0.8em', backgroundColor: '#4A5568', borderRadius: '4px'}} />
             </UserDetails>
          </UserInfo>
        ) : (fullName || userEmailFromContext) ? (
          <UserInfo>
            <ProfileImageContainer>
              {animatingImageUrl ? (
                <AnimatedProfileImage
                  src={animatingImageUrl}
                  alt="Uploading..."
                  onAnimationEnd={() => completeImageAnimation(animatingImageUrl)}
                />
              ) : profileImageUrl ? (
                <ProfileImage
                  key="sidebar_profile_img"
                  src={`${API_BASE}${profileImageUrl}`}
                  alt="User Profile"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                />
              ) : (
                <div style={{width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', color: 'white', fontWeight: 'bold', textAlign: 'center'}}>
                  {fullName ? fullName.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase() : (userEmailFromContext ? userEmailFromContext[0].toUpperCase() : '?')}
                </div>
              )}
            </ProfileImageContainer>
            <UserDetails>
              <UserName title={getFormattedName() || fullName || ''}>{getFormattedName() || fullName || 'User Name'}</UserName>
              {userEmailFromContext && <UserEmailStyled title={userEmailFromContext}>{userEmailFromContext}</UserEmailStyled>}
            </UserDetails>
          </UserInfo>
        ) : (
          <UserInfo>
             <ProfileImageContainer>
                <div style={{width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', color: 'white', fontWeight: 'bold', textAlign: 'center'}}>
                  ?
                </div>
             </ProfileImageContainer>
             <UserDetails>
                <UserName>User</UserName>
             </UserDetails>
          </UserInfo>
        )}
        
        <SessionTimerBadge 
          $isWarning={isWarning} 
          onClick={() => setShowModal(true)}
          title="Session Time Remaining - Click to extend"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12,6 12,12 16,14"/>
          </svg>
          {formatTime(timeLeft)}
        </SessionTimerBadge>
        <LogoutButton
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleLogout}
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          Logout
        </LogoutButton>
      </Sidebar>
      
      <Content>
        {activeView === 'applications' ? (
          <>
            <Header>
              <Title
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                My Applications
                {!isLoading && applications.length > 0 && (
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    marginLeft: '10px', minWidth: '28px', height: '28px', padding: '0 8px',
                    borderRadius: '14px', fontSize: '0.82rem', fontWeight: 700,
                    background: 'linear-gradient(135deg, #4c6ef5 0%, #3b5bdb 100%)',
                    color: '#fff', WebkitTextFillColor: '#fff', verticalAlign: 'middle',
                    boxShadow: '0 2px 6px rgba(76,110,245,0.3)', letterSpacing: '0'
                  }}>
                    {applications.length}
                  </span>
                )}
              </Title>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                  padding: '6px 14px', borderRadius: '8px',
                  background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
                  border: '1px solid #fbbf24',
                  fontSize: '12.5px', fontWeight: 700, color: '#92400e',
                  whiteSpace: 'nowrap',
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#b45309" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                  </svg>
                  Application Deadline 31st March
                </span>
                <button
                  onClick={() => setShowPuneMapModal(true)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '5px',
                    padding: '6px 14px', borderRadius: '8px', border: '1px solid #c4b5fd',
                    background: 'linear-gradient(135deg, #ede9fe, #ddd6fe)',
                    color: '#6d28d9', fontSize: '12.5px', fontWeight: 700,
                    cursor: 'pointer', whiteSpace: 'nowrap',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'linear-gradient(135deg, #ddd6fe, #c4b5fd)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'linear-gradient(135deg, #ede9fe, #ddd6fe)'; }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                  View Pune-GA Map
                </button>
                <CreateButton 
                  onClick={handleCreateApplication}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  + Create New Application
                </CreateButton>
              </div>
            </Header>
            
            <div style={{
              display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap',
              padding: '10px 16px', marginBottom: '16px',
              background: 'linear-gradient(135deg, #f0f4ff 0%, #e8f0fe 100%)',
              borderRadius: '10px', border: '1px solid #dbe4f8'
            }}>
              <span style={{ fontSize: '13px', color: '#475569', fontWeight: 500 }}>
                Know someone interested in a CNG dealership? Spread the word!
              </span>
              <SocialShareButtons variant="inline" size={30} showLabel={false} />
            </div>

            {isLoading ? (
              <Card>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '2rem' }}>
                  <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
                  <h3>Loading Applications...</h3>
                  <p>Please wait while we fetch your applications.</p>
                </div>
              </Card>
            ) : error ? (
              <Card>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '2rem' }}>
                  <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⚠️</div>
                  <h3>Error Loading Applications</h3>
                  <p>{error}</p>
                </div>
              </Card>
            ) : applications.length === 0 ? (
              showEligibilityGuide ? (
              <Card>
                <div style={{ padding: '2rem', maxWidth: '600px', margin: '0 auto' }}>
                  {/* Step header */}
                  <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                    <div style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      width: '48px', height: '48px', borderRadius: '14px',
                      background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                      marginBottom: '12px', fontSize: '1.4rem',
                      boxShadow: '0 6px 16px rgba(245, 158, 11, 0.3)',
                    }}>📍</div>
                    <h3 style={{ margin: '0 0 6px', fontSize: '1.15rem', fontWeight: 800, color: '#1e293b' }}>
                      Step 1: Know Your Land Eligibility
                    </h3>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b', lineHeight: 1.5 }}>
                      Before starting your application, please check if the land you want to offer to MNGL
                      for CNG Dealership falls under the <strong>Advertised Locations in Pune</strong>.
                    </p>
                  </div>

                  {/* Check eligibility CTA */}
                  <div style={{
                    background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
                    border: '1.5px solid #fbbf24', borderRadius: '14px',
                    padding: '18px 20px', textAlign: 'center', marginBottom: '16px',
                  }}>
                    <div style={{ fontSize: '0.88rem', fontWeight: 700, color: '#92400e', marginBottom: '12px' }}>
                      🤔 Does your offered land come under MNGL's advertised areas?
                    </div>
                    <a
                      href="/area-stretch-info"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: '8px',
                        padding: '10px 24px', borderRadius: '10px',
                        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                        color: '#fff', textDecoration: 'none', fontSize: '0.88rem', fontWeight: 700,
                        boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)', transition: 'all 0.3s',
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
                      </svg>
                      Check Advertised Locations (Pune Only)
                    </a>
                  </div>

                  {/* Confused / Help section */}
                  <div style={{
                    background: '#f0f4ff', border: '1px solid #dbeafe', borderRadius: '14px',
                    padding: '16px 18px', marginBottom: '20px',
                  }}>
                    <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#1e40af', marginBottom: '10px' }}>
                      😕 Confused or not sure? We're here to help!
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '0.8rem', color: '#475569' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '1rem' }}>💬</span>
                        <span>Chat with us via the <strong>"Ask Agent!"</strong> button at the bottom-right corner</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '1rem' }}>📞</span>
                        <span>Call helpline: <strong style={{ color: '#1e40af' }}>095525 71239</strong> or <strong style={{ color: '#1e40af' }}>094041 52755</strong></span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '1rem' }}>✉️</span>
                        <span>Email: <a href="mailto:cngdealership2026@mngl.in" style={{ color: '#2563eb', fontWeight: 600 }}>cngdealership2026@mngl.in</a></span>
                      </div>
                    </div>
                  </div>

                  {/* Proceed anyway */}
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginBottom: '10px' }}>
                      Already checked and want to proceed?
                    </div>
                    <button
                      onClick={() => setEligibilityAcknowledged(true)}
                      style={{
                        padding: '11px 28px', borderRadius: '10px', border: 'none',
                        background: 'linear-gradient(135deg, #4c6ef5 0%, #3b5bdb 100%)',
                        color: '#fff', fontSize: '0.88rem', fontWeight: 700, cursor: 'pointer',
                        boxShadow: '0 4px 14px rgba(76, 110, 245, 0.3)', transition: 'all 0.3s',
                      }}
                    >
                      ✅ I understand, let me start my application
                    </button>
                  </div>
                </div>
              </Card>
              ) : (
              <Card>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '2rem' }}>
                  <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>📄</div>
                  <h3>No Applications Found</h3>
                  <p>You haven't created any applications yet.</p>
                  <CreateButton 
                    onClick={handleCreateApplication}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    style={{ marginTop: '20px' }}
                  >
                    + Create Your First Application
                  </CreateButton>
                </div>
              </Card>
              )
            ) : (
              <Card style={{ padding: '2px 2rem 2rem' }}>
                {applications.length > 5 && (() => {
                  const total = applications.length;
                  const ps = appPageSize === 'all' ? total : (appPageSize as number);
                  const totalPages = appPageSize === 'all' ? 1 : Math.ceil(total / ps);
                  return (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.6rem 0.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 500 }}>Show</span>
                        <select
                          value={String(appPageSize)}
                          onChange={e => { const v = e.target.value; setAppPageSize(v === 'all' ? 'all' : Number(v)); setAppPage(1); }}
                          style={{
                            padding: '5px 24px 5px 10px', border: '1.5px solid #e2e8f0', borderRadius: '8px',
                            fontSize: '0.8rem', color: '#334155', fontWeight: 600, cursor: 'pointer',
                            background: '#fff url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'10\' height=\'6\'%3E%3Cpath d=\'M0 0l5 6 5-6z\' fill=\'%2394a3b8\'/%3E%3C/svg%3E") no-repeat right 8px center',
                            appearance: 'none', WebkitAppearance: 'none'
                          }}
                        >
                          <option value="5">5</option>
                          <option value="10">10</option>
                          <option value="15">15</option>
                          <option value="all">All</option>
                        </select>
                        <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>of {total}</span>
                      </div>
                      {totalPages > 1 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <button
                            disabled={appPage <= 1}
                            onClick={() => setAppPage(p => p - 1)}
                            style={{
                              width: '30px', height: '30px', borderRadius: '50%', border: '1.5px solid #e2e8f0',
                              background: appPage <= 1 ? '#f8fafc' : '#fff', color: appPage <= 1 ? '#cbd5e1' : '#475569',
                              cursor: appPage <= 1 ? 'not-allowed' : 'pointer', fontSize: '0.85rem', fontWeight: 600,
                              display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s'
                            }}
                          >&lsaquo;</button>
                          {Array.from({ length: totalPages }, (_, i) => i + 1).map(pg => (
                            <button
                              key={pg}
                              onClick={() => setAppPage(pg)}
                              style={{
                                width: '30px', height: '30px', borderRadius: '50%', fontSize: '0.78rem', fontWeight: 700,
                                border: pg === appPage ? '2px solid #4c6ef5' : '1.5px solid #e2e8f0',
                                background: pg === appPage ? 'linear-gradient(135deg, #4c6ef5, #3b5bdb)' : '#fff',
                                color: pg === appPage ? '#fff' : '#475569',
                                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                transition: 'all 0.2s', boxShadow: pg === appPage ? '0 2px 8px rgba(76,110,245,0.3)' : 'none'
                              }}
                            >{pg}</button>
                          ))}
                          <button
                            disabled={appPage >= totalPages}
                            onClick={() => setAppPage(p => p + 1)}
                            style={{
                              width: '30px', height: '30px', borderRadius: '50%', border: '1.5px solid #e2e8f0',
                              background: appPage >= totalPages ? '#f8fafc' : '#fff', color: appPage >= totalPages ? '#cbd5e1' : '#475569',
                              cursor: appPage >= totalPages ? 'not-allowed' : 'pointer', fontSize: '0.85rem', fontWeight: 600,
                              display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s'
                            }}
                          >&rsaquo;</button>
                        </div>
                      )}
                    </div>
                  );
                })()}
                <ApplicationsTable>
                  <thead>
                    <tr>
                      <th>Application ID</th>
                      <th>Status</th>
                      <th>Updated</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(appPageSize === 'all' ? applications : applications.slice((appPage - 1) * (appPageSize as number), appPage * (appPageSize as number))).map((application) => (
                      <tr key={application.applicationId}>
                        <td>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                            {lockedApplications.has(application.applicationId) && (
                              <LockIcon 
                                style={{ 
                                  fontSize: '14px', 
                                  color: '#f59e0b',
                                  filter: 'drop-shadow(0 0 2px rgba(245, 158, 11, 0.5))'
                                }} 
                                titleAccess="Application is locked"
                              />
                            )}
                            {application.applicationId}
                          </span>
                        </td>
                        <td>
                          <StatusBadge status={application.status}>
                            {application.status}
                          </StatusBadge>
                        </td>
                        <td>{format(new Date(application.updatedAt), 'MMM dd, yyyy')}</td>
                        <td>
                          <div style={{ display: 'flex', gap: '8px' }} key={`actions-${application.applicationId}`}>
                            {application.status && application.status.toLowerCase() === 'draft' ? (
                              <>
                                <StyledActionButton 
                                  onClick={() => handleEditApplication(application.applicationId)}
                                  title="Resume Application"
                                >
                                  <EditIcon />
                                </StyledActionButton>
                                <StyledActionButton 
                                  onClick={() => {
                                    setSelectedApplicationId(application.applicationId);
                                    setActiveView('files');
                                  }}
                                  title="Manage Files"
                                >
                                  <FolderIcon />
                                </StyledActionButton>
                                <StyledActionButton 
                                  onClick={() => handleDownloadApplication(application)}
                                  title="Download Application"
                                >
                                  <DownloadIcon />
                                </StyledActionButton>
                                <StyledActionButton 
                                  onClick={() => handleDeleteDraft(application.applicationId)}
                                  title="Delete Draft"
                                  style={{ color: '#e53e3e', backgroundColor: 'rgba(229, 62, 62, 0.1)' }}
                                >
                                  <DeleteOutlineIcon />
                                </StyledActionButton>
                              </>
                            ) : (
                              <>
                                <StyledActionButton 
                                  onClick={() => handleViewApplication(application.applicationId)}
                                  title="View Application"
                                >
                                  <VisibilityIcon />
                                </StyledActionButton>
                                <StyledActionButton 
                                  onClick={() => {
                                    setSelectedApplicationId(application.applicationId);
                                    setActiveView('files');
                                  }}
                                  title="Manage Files"
                                >
                                  <FolderIcon />
                                </StyledActionButton>
                                <StyledActionButton 
                                  onClick={() => handleDownloadApplication(application)}
                                  title="Download as PDF"
                                >
                                  <DownloadIcon />
                                </StyledActionButton>
                                {application.status === 'payment_complete' && (
                                  <StyledActionButton 
                                    onClick={() => handleViewThankYou(application.applicationId)}
                                    title="View Thank You Page"
                                    style={{ color: '#28a745', backgroundColor: 'rgba(40, 167, 69, 0.1)' }}
                                  >
                                    <CheckCircleIcon />
                                  </StyledActionButton>
                                )}
                                {application.status !== 'Queued for deletion' && (
                                  <StyledActionButton 
                                    onClick={() => handleRequestDeletion(application.applicationId)}
                                    title="Request Deletion"
                                    style={{ color: '#e53e3e', backgroundColor: 'rgba(229, 62, 62, 0.1)' }}
                                  >
                                    <DeleteOutlineIcon />
                                  </StyledActionButton>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </ApplicationsTable>
              </Card>
            )}
          </>
        ) : activeView === 'files' ? (
          <>
            <Header>
              <Title
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                File Management
              </Title>
              <CreateButton 
                onClick={() => setActiveView('applications')}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                Back to Applications
              </CreateButton>
            </Header>
            
            {applications.length === 0 ? (
              <Card>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '2rem' }}>
                  <div style={{ fontSize: '2rem' }}>📂</div>
                  <h3>No Applications Found</h3>
                  <p>You haven't created any applications yet.</p>
                </div>
              </Card>
            ) : (
              <Card>
                <div style={{ padding: '1rem' }}>
                  <h3 style={{ marginBottom: '1rem' }}>Your Applications</h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                    {applications.map((application) => (
                      <div 
                        key={application.applicationId} 
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          cursor: 'pointer',
                          padding: '1rem',
                          borderRadius: '8px',
                          backgroundColor: selectedApplicationId === application.applicationId ? '#edf2f7' : 'transparent',
                          width: '150px',
                          transition: 'all 0.2s ease',
                          position: 'relative'
                        }}
                        onClick={() => setSelectedApplicationId(application.applicationId)}
                      >
                        {application.dealershipGroupId && (
                          <div style={{
                            position: 'absolute',
                            top: '0.5rem',
                            right: '0.5rem',
                            backgroundColor: '#667eea',
                            color: 'white',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '12px',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                          }}>
                            🔗 {linkedCounts[application.applicationId] || 0}
                          </div>
                        )}
                        {application.isPrimaryApplicant && application.dealershipGroupId && (
                          <div style={{
                            position: 'absolute',
                            top: '0.5rem',
                            left: '0.5rem',
                            backgroundColor: '#ffd700',
                            color: '#7c5a00',
                            padding: '0.2rem 0.4rem',
                            borderRadius: '4px',
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                          }}>
                            PRIMARY
                          </div>
                        )}
                        <FolderIcon style={{ fontSize: '3rem', color: '#4299e1', marginBottom: '0.5rem' }} />
                        <div style={{ fontSize: '0.9rem', fontWeight: 'bold', textAlign: 'center', wordBreak: 'break-word' }}>
                          {application.applicationId}
                        </div>
                        <StatusBadge status={application.status} style={{ marginTop: '0.5rem' }}>
                          {application.status}
                        </StatusBadge>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            )}
            
            {selectedApplicationId && (
              <>
                <div style={{ marginTop: '1rem' }}>
                  <Card>
                    <div style={{ padding: '1rem', borderBottom: '1px solid #e2e8f0' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h3 style={{ margin: 0 }}>Files for Application: {selectedApplicationId}</h3>
                        <CreateButton 
                          onClick={() => setSelectedApplicationId(null)}
                          whileHover={{ scale: 1.03 }}
                          whileTap={{ scale: 0.97 }}
                          style={{ fontSize: '0.9rem', padding: '0.5rem 1rem' }}
                        >
                          Back to Folders
                        </CreateButton>
                      </div>
                    </div>
                  </Card>
                </div>
                <ApplicationFiles applicationId={selectedApplicationId} />
              </>
            )}
          </>
        ) : activeView === 'linked' ? (
          <>
            <Header>
              <Title
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                Linked Applications
              </Title>
            </Header>
            
            {isLoadingLinked ? (
              <Card>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '2rem' }}>
                  <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
                  <h3>Loading Linked Applications...</h3>
                  <p>Please wait while we fetch linked application details.</p>
                </div>
              </Card>
            ) : linkedApplicationsDetails.length === 0 ? (
              <Card>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '2rem' }}>
                  <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>🔗</div>
                  <h3>No Linked Applications</h3>
                  <p>You don't have any applications linked with other applicants yet.</p>
                  <p style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#666' }}>
                    To link your application with another applicant for joint ownership, go to your application form and use the "Joint Application" section.
                  </p>
                </div>
              </Card>
            ) : (
              <Card>
                <div style={{ padding: '1rem' }}>
                  <p style={{ marginBottom: '1.5rem', color: '#666', fontSize: '0.95rem' }}>
                    Below are all applications linked with yours for joint dealership ownership. 
                    The <strong style={{ color: '#b8860b' }}>PRIMARY</strong> applicant is the main contact for the dealership.
                  </p>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1.5rem' }}>
                    {linkedApplicationsDetails.map((linkedApp: any) => {
                      const isMyApp = applications.some(app => app.applicationId === linkedApp.applicationId);
                      const phoneNumber = linkedApp.phoneNumber || '';
                      const maskedPhone = phoneNumber.length >= 4 
                        ? `****${phoneNumber.slice(-4)}` 
                        : phoneNumber;
                      
                      return (
                        <div
                          key={linkedApp.applicationId}
                          style={{
                            border: isMyApp ? '2px solid #667eea' : '1px solid #e2e8f0',
                            borderRadius: '12px',
                            padding: '1.5rem',
                            backgroundColor: isMyApp ? '#f7fafc' : 'white',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            position: 'relative',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          {linkedApp.isPrimaryApplicant && (
                            <div style={{
                              position: 'absolute',
                              top: '0.75rem',
                              right: '0.75rem',
                              backgroundColor: '#ffd700',
                              color: '#7c5a00',
                              padding: '0.3rem 0.6rem',
                              borderRadius: '6px',
                              fontSize: '0.7rem',
                              fontWeight: 700,
                              boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem'
                            }}>
                              ⭐ PRIMARY
                            </div>
                          )}
                          
                          {isMyApp && (
                            <div style={{
                              position: 'absolute',
                              top: '0.75rem',
                              left: '0.75rem',
                              backgroundColor: '#667eea',
                              color: 'white',
                              padding: '0.3rem 0.6rem',
                              borderRadius: '6px',
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              boxShadow: '0 2px 4px rgba(0,0,0,0.15)'
                            }}>
                              YOU
                            </div>
                          )}
                          
                          <div style={{ marginTop: linkedApp.isPrimaryApplicant || isMyApp ? '2rem' : '0' }}>
                            <h3 style={{ 
                              fontSize: '1.1rem', 
                              fontWeight: 600, 
                              marginBottom: '0.75rem',
                              color: '#2d3748'
                            }}>
                              {linkedApp.fullName || 'N/A'}
                            </h3>
                            
                            <div style={{ 
                              display: 'flex', 
                              flexDirection: 'column', 
                              gap: '0.5rem',
                              fontSize: '0.9rem'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span style={{ fontWeight: 600, color: '#4a5568', minWidth: '90px' }}>App ID:</span>
                                <span style={{ 
                                  fontFamily: 'monospace', 
                                  fontSize: '0.85rem',
                                  color: '#2d3748'
                                }}>
                                  {linkedApp.applicationId}
                                </span>
                              </div>
                              
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span style={{ fontWeight: 600, color: '#4a5568', minWidth: '90px' }}>Phone:</span>
                                <span style={{ 
                                  fontFamily: 'monospace',
                                  fontSize: '0.85rem',
                                  color: '#2d3748',
                                  letterSpacing: '0.5px'
                                }}>
                                  {maskedPhone}
                                </span>
                              </div>
                              
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span style={{ fontWeight: 600, color: '#4a5568', minWidth: '90px' }}>Status:</span>
                                <StatusBadge status={linkedApp.status}>
                                  {linkedApp.status}
                                </StatusBadge>
                              </div>
                              
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span style={{ fontWeight: 600, color: '#4a5568', minWidth: '90px' }}>Group ID:</span>
                                <span style={{ 
                                  fontSize: '0.75rem',
                                  color: '#718096',
                                  fontFamily: 'monospace',
                                  backgroundColor: '#edf2f7',
                                  padding: '0.2rem 0.4rem',
                                  borderRadius: '4px'
                                }}>
                                  {linkedApp.groupId}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  <div style={{
                    marginTop: '2rem',
                    padding: '1rem',
                    backgroundColor: '#f7fafc',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <h4 style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: '0.5rem', color: '#2d3748' }}>
                      ℹ️ Privacy Notice
                    </h4>
                    <p style={{ fontSize: '0.85rem', color: '#718096', margin: 0 }}>
                      For security reasons, phone numbers are partially masked. Only the last 4 digits are visible. 
                      Full contact details are available to administrators only.
                    </p>
                  </div>
                </div>
              </Card>
            )}
          </>
        ) : activeView === 'profile' ? (
          <>
            <Header>
              <Title
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                User Profile
              </Title>
            </Header>
            <UserProfileSettings />
          </>
        ) : null}
      </Content>
      {showDeleteModal && (
        <ModalOverlay>
          <ModalContent whileHover={{ scale: 1.01 }} initial={{ opacity: 0, y: -50 }} animate={{ opacity: 1, y: 0 }}>
            <ModalHeader>
              {/* <FaExclamationTriangle style={{ marginRight: '10px', color: '#f39c12' }} size={24} /> */}
              Confirm Deletion
            </ModalHeader>
            <ModalBody>
              <p>Are you sure you want to permanently delete this draft application (ID: <strong>{applicationToDeleteId}</strong>)? This action cannot be undone.</p>
              <p>To confirm, please type "DELETE" in the box below:</p>
              <ModalInput
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder='Type DELETE'
              />
            </ModalBody>
            <ModalFooter>
              <ModalButton onClick={() => setShowDeleteModal(false)} style={{ backgroundColor: '#7f8c8d' }}>
                Cancel
              </ModalButton>
              <ModalButton 
                onClick={handleConfirmDeleteDraft} 
                disabled={deleteConfirmText !== 'DELETE'} 
                style={deleteConfirmText !== 'DELETE' ? { backgroundColor: '#bdc3c7', cursor: 'not-allowed'} : { backgroundColor: '#e74c3c'}}
              >
                Confirm Delete
              </ModalButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
    <ChatWidget
      userEmail={userEmailFromContext}
      userName={fullName || getFormattedName(fullName)}
      userPhone={userPhoneFromContext}
      mode="user"
    />
    <PuneMapModal open={showPuneMapModal} onClose={() => setShowPuneMapModal(false)} />
    </>
  );
};

// Additional Styled Components for Modal
const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled(motion.div)`
  background: white;
  padding: 25px;
  border-radius: 12px;
  box-shadow: 0 5px 20px rgba(0,0,0,0.2);
  width: 90%;
  max-width: 500px;
  text-align: left;
  border-top: 5px solid #e74c3c; /* Accent color */
`;

const ModalHeader = styled.h3`
  margin-top: 0;
  margin-bottom: 20px;
  font-size: 1.4rem;
  color: #34495e;
  display: flex;
  align-items: center;
`;

const ModalBody = styled.div`
  margin-bottom: 20px;
  font-size: 1rem;
  color: #555;
  line-height: 1.6;
  p {
    margin-bottom: 10px;
  }
`;

const ModalInput = styled.input`
  width: 100%;
  padding: 12px;
  margin-top: 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  box-sizing: border-box;
  font-size: 1rem;
  &:focus {
    border-color: #3498db;
    outline: none;
    box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.2);
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const ModalButton = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 500;
  transition: background-color 0.2s ease;
  color: white;
  &:hover {
    opacity: 0.9;
  }
`;

export default UserDashboard;
